import { useEffect, useRef, useState } from 'react';
import { FIBERS, STITCHES, WEIGHTS } from '../../domain/catalogs';
import { Button } from '../../components/ui';
import { Brush, Trash, X } from '../../components/icons';
import type { EditorApi } from './useEditor';

const selClass = 'w-full rounded-lg border border-border bg-surface-2 px-2.5 py-2 text-sm outline-none focus-visible:border-accent';

export function Loupe({
  editor,
  cell,
  anchor,
  onClose,
}: {
  editor: EditorApi;
  cell: { r: number; c: number };
  anchor: { x: number; y: number };
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [, force] = useState(0);
  const d = editor.getCell(cell.r, cell.c);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const set = (patch: Parameters<EditorApi['editCell']>[2]) => {
    editor.editCell(cell.r, cell.c, patch);
    force((n) => n + 1);
  };

  const w = 280;
  const left = Math.max(8, Math.min(window.innerWidth - w - 8, anchor.x - w / 2));
  const top = Math.min(window.innerHeight - 360, anchor.y + 12);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Edit stitch row ${cell.r + 1} column ${cell.c + 1}`}
      className="fixed z-50 w-[280px] rounded-2xl border border-border bg-elevated p-3.5"
      style={{ left, top, boxShadow: 'var(--shadow)' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="font-mono text-sm font-bold">
          R<span className="text-accent">{cell.r + 1}</span>·C<span className="text-accent">{cell.c + 1}</span>
        </div>
        <Button variant="ghost" iconOnly aria-label="Close" onClick={onClose} className="h-7 w-7">
          <X width={13} height={13} />
        </Button>
      </div>

      <Label>Yarn colour</Label>
      <div className="mb-2 grid grid-cols-9 gap-1">
        <button
          title="No yarn"
          onClick={() => set({ color: null })}
          className="flex aspect-square items-center justify-center rounded-md border border-border bg-surface-2"
          style={{ outline: !d.color ? '2px solid var(--accent)' : undefined, outlineOffset: 1 }}
        >
          <span className="block h-px w-3/4 rotate-[-45deg] bg-danger" />
        </button>
        {editor.palette.map((y) => (
          <button
            key={y.id}
            title={y.name}
            onClick={() => set({ color: y.id })}
            className="aspect-square rounded-md border border-black/25"
            style={{ background: y.hex, outline: d.color === y.id ? '2px solid var(--accent)' : undefined, outlineOffset: 1 }}
          />
        ))}
      </div>

      <Label>Fibre / fabric</Label>
      <select className={`mb-2 ${selClass}`} value={d.fiber ?? ''} onChange={(e) => set({ fiber: e.target.value || null })}>
        <option value="">Choose fibre…</option>
        {FIBERS.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name} — {f.desc}
          </option>
        ))}
      </select>

      <Label>Stitch</Label>
      <select className={`mb-2 ${selClass}`} value={d.stitch ?? ''} onChange={(e) => set({ stitch: e.target.value || null })}>
        <option value="">Knit (plain)</option>
        {STITCHES.map((s) => (
          <option key={s.id} value={s.id}>
            {s.sym ? `${s.sym} ` : ''}
            {s.name} — {s.short}
          </option>
        ))}
      </select>

      <Label>Weight</Label>
      <select className={`mb-2 ${selClass}`} value={d.weight ?? ''} onChange={(e) => set({ weight: e.target.value || null })}>
        <option value="">Project default</option>
        {WEIGHTS.map((wt) => (
          <option key={wt.id} value={wt.id}>
            {wt.name} — {wt.desc}
          </option>
        ))}
      </select>

      <Label>Note</Label>
      <textarea
        className={`${selClass} resize-none`}
        rows={2}
        maxLength={120}
        placeholder="e.g. carry yarn loosely…"
        defaultValue={d.note ?? ''}
        onBlur={(e) => set({ note: e.target.value || null })}
      />

      <div className="mt-2.5 flex gap-2">
        <Button
          variant="secondary"
          className="flex-1"
          onClick={() => {
            if (d.color) {
              editor.setActiveYarn(d.color);
              editor.setTool('brush');
            }
          }}
        >
          <Brush width={13} height={13} /> Use as brush
        </Button>
        <Button variant="danger" className="flex-1" onClick={() => set({ color: null, fiber: null, stitch: null, weight: null, note: null })}>
          <Trash width={13} height={13} /> Clear
        </Button>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 mt-2 text-[10px] font-semibold uppercase tracking-wider text-text-3">{children}</div>;
}
